import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import './AdminNavbar.css';

const AdminNavbar = ({ activeTab, setActiveTab, selectedPatient, onBackToList }) => {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    const [showUserMenu, setShowUserMenu] = useState(false);
    const menuRef = useRef(null);

    // Close dropdown when clicking outside
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (menuRef.current && !menuRef.current.contains(event.target)) {
                setShowUserMenu(false);
            }
        };

        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, []);

    const getUserRole = () => {
        if (user?.role === 'admin') return 'Admin';
        if (user?.role === 'doctor') return 'Doctor';
        return 'User';
    };

    const handleLogout = async () => {
        setShowUserMenu(false); // Close menu first

        const confirmLogout = window.confirm(
            'Are you sure you want to logout?\n\nYou will be redirected to the login page.'
        );

        if (!confirmLogout) {
            return;
        }

        try {
            await logout();
            navigate('/login', { replace: true });
        } catch (error) {
            console.error('Logout error:', error);
            // Force redirect even if logout fails
            navigate('/login', { replace: true });
        }
    };

    const handlePatientView = () => {
        setShowUserMenu(false); // Close menu first
        navigate('/dashboard');
    };

    return (
        <nav className="admin-navbar">
            <div className="admin-navbar-brand">
                <h2>Dr. Within Health Journal</h2>
                <span className="admin-badge">Admin Panel</span>
            </div>

            <div className="admin-navbar-nav">
                {selectedPatient ? (
                    <div className="patient-breadcrumb">
                        <button
                            onClick={onBackToList}
                            className="back-btn"
                        >
                            ← Back to Patients
                        </button>
                        <span className="current-patient">
                            {selectedPatient.name}
                        </span>
                    </div>
                ) : (
                    <>
                        <button
                            className={`nav-tab ${activeTab === 'patients' ? 'active' : ''}`}
                            onClick={() => setActiveTab('patients')}
                        >
                            <span className="nav-icon">👥</span>
                            Patients
                        </button>
                        <button
                            className={`nav-tab ${activeTab === 'consents' ? 'active' : ''}`}
                            onClick={() => setActiveTab('consents')}
                        >
                            <span className="nav-icon">📄</span>
                            Consent Forms
                        </button>
                        <button
                            className={`nav-tab ${activeTab === 'analytics' ? 'active' : ''}`}
                            onClick={() => setActiveTab('analytics')}
                        >
                            <span className="nav-icon">📊</span>
                            Analytics
                        </button>
                        {user?.role === 'doctor' && (
                            <button
                                className={`nav-tab ${activeTab === 'payments' ? 'active' : ''}`}
                                onClick={() => setActiveTab('payments')}
                            >
                                <span className="nav-icon">💳</span>
                                Payments
                            </button>
                        )}
                    </>
                )}
            </div>

            <div className="admin-navbar-user">
                <button
                    onClick={handlePatientView}
                    className="user-dashboard-link"
                    title="Switch to Patient View"
                >
                    👤 Patient View
                </button>

                <div className="user-menu-container" ref={menuRef}>
                    <button
                        className="user-menu-toggle"
                        onClick={() => setShowUserMenu(!showUserMenu)}
                        title="User Menu"
                    >
                        <span className="user-info">
                            <span className="user-role">{getUserRole()}</span>
                            <span className="user-name">{user?.fullName || user?.email}</span>
                        </span>
                        <div className="user-avatar">
                            <span>{user?.role === 'admin' ? 'Ad' : 'Dr'}</span>
                        </div>
                    </button>

                    {showUserMenu && (
                        <div className="user-dropdown">
                            <div className="dropdown-header">
                                <strong>{user?.fullName || user?.email}</strong>
                                <small>{getUserRole()}</small>
                            </div>
                            <div className="dropdown-divider"></div>
                            <button
                                onClick={handlePatientView}
                                className="dropdown-item"
                            >
                                👤 Switch to Patient View
                            </button>
                            <div className="dropdown-divider"></div>
                            <button
                                onClick={handleLogout}
                                className="dropdown-item logout-item"
                            >
                                ⏻ Logout
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </nav>
    );
};

export default AdminNavbar;